import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DataTableColumn } from '~/components/data-table/data-table.component';
import { UploadFile } from '~/services/upload-file/upload-file';
import { ValidatorService, ReviewRecord } from '~/services/validator.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit {

    @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<any>;
    public selectedFileName: string | null = '';
    public SelectedFile: File | null = null;
    public fullSasUrl: string = '';
    public batchId: string = '';
    private sasUrl: string | null = null;
    public fileUploadedFlag: boolean = false;
    private readonly _snackBar = inject(MatSnackBar);

    constructor(
        private readonly router: Router,
        private readonly validatorService: ValidatorService,
        private readonly uploadFile: UploadFile,
        public dialog: MatDialog
    ) { }

    public readonly recordDetailsColumns: DataTableColumn<ReviewRecord>[] = [
        { key: 'record_id', header: 'Record ID' },
        { key: 'batch_id', header: 'Batch ID' },
        { key: 'status', header: 'Status' },
        { key: 'total_files', header: 'Total Files', align: 'right' },
        { key: 'pending_review', header: 'Pending Review', align: 'right' }
    ];

    public readonly pageSize: number = 10;
    public currentPage: number = 1;
    public searchQuery: string = '';
    public isLoading: boolean = false;
    public errorMessage: string | null = null;
    public reviews: ReviewRecord[] = [];

    public get filteredReviews(): ReviewRecord[] {
        const query = this.searchQuery.trim().toLowerCase();
        if (!query) {
            return this.reviews;
        }

        return this.reviews.filter((r) =>
            r.record_id.toLowerCase().includes(query)
        );
    }

    public get totalRecords(): number {
        return this.filteredReviews.length;
    }

    public get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
    }

    public get pagedReviews(): ReviewRecord[] {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredReviews.slice(start, start + this.pageSize);
    }

    public get startRecordIndex(): number {
        if (this.totalRecords === 0) {
            return 0;
        }

        return (this.currentPage - 1) * this.pageSize + 1;
    }

    public get endRecordIndex(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalRecords);
    }

    /**
     * Lifecycle hook that is called after the component has been initialized. It triggers the initial fetch of reviews from the backend service to populate the data table displayed on the home page. This method ensures that the component starts with the most up-to-date data when it is rendered for the user.
     */
    ngOnInit(): void {
        this.fetchReviews();
    }

    /**
     * Fetches the list of reviews from the backend service and updates the component state accordingly. It sets the loading state while the request is in progress and handles any errors that may occur during the fetch operation, displaying an appropriate error message to the user if needed.
     */
    private fetchReviews(): void {
        this.isLoading = true;
        this.errorMessage = null;

        this.validatorService.getReviews('pending_review', 50, 0).subscribe({
            next: (response) => {
                this.reviews = response.reviews;
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error fetching reviews:', error);
                this.errorMessage = 'Failed to load reviews. Please try again.';
                this.isLoading = false;
            }
        });
    }

    /**
     * Navigates to the previous page of reviews if the current page is greater than 1. This function is typically called when the user clicks on a "Previous" button in the pagination controls. It updates the `currentPage` property to reflect the new page number, which in turn updates the displayed reviews based on the pagination logic defined in the component.
     */
    public goToPreviousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
        }
    }

    /**
     * Navigates to the next page of reviews if the current page is less than the total number of pages. This function is typically called when the user clicks on a "Next" button in the pagination controls. It updates the `currentPage` property to reflect the new page number, which in turn updates the displayed reviews based on the pagination logic defined in the component.
     */
    public goToNextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage += 1;
        }
    }

    /**
     * @param value - The new search query entered by the user. This function updates the `searchQuery` property with the new value and resets the `currentPage` to 1 to ensure that the user sees the first page of results that match the new search criteria. It is typically called when the user types into a search input field in the UI.
     */
    public onSearchChange(value: string): void {
        this.searchQuery = value;
        this.currentPage = 1;
    }

    /**
     * @param row - The review record that was clicked by the user. This function navigates to the record details page for the selected review, passing the record data in the navigation state. It is typically called when the user clicks on a record ID in the data table, allowing them to view more detailed information about that specific review.
     */
    public onRecordIdClick(row: ReviewRecord): void {
        this.router.navigate(['/record', row.record_id], {
            state: { record: row }
        });
    }

    /** Placeholder function for file upload - to be implemented with actual file input and handling logic */
    public uploadFiles() {
        this.isLoading = true;
        this.uploadFile.updateClassification().subscribe({
            next: (response) => {
                this.isLoading = false;
                this.sasUrl = response.sas_url;
                this.batchId = response.batch_id;
                this.dialog.open(this.dialogTemplate, {
                    width: '50%',
                    height: '50%',
                });
                this.isLoading = false;
                this.selectedFileName = '';
                this.SelectedFile = null;
                this.fullSasUrl = '';
            },
            error: (error) => {
                console.error('Error fetching reviews:', error);
            }
        })
    }

    /**
     * @param event - The file input change event
     */
    public onFileSelected(event: Event): void {
        this.fileUploadedFlag = false;
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.SelectedFile = input.files[0];
            this.selectedFileName = this.SelectedFile.name;

            // Check if the selected file is a .docx file
            if (!this.selectedFileName.toLowerCase().endsWith('.docx')) {
                alert('Only .docx files are allowed.');
                this.SelectedFile = null;
                this.selectedFileName = null;
                return;
            }

            // Encode the filename to handle spaces and special characters
            const encodedFileName = encodeURIComponent(this.selectedFileName ?? '');
            if (this.sasUrl) {
                const urlParts = this.sasUrl.split('?');
                const baseUrl = urlParts[0];
                const queryString = urlParts[1];
                this.fullSasUrl = `${baseUrl}/${encodedFileName}?${queryString}`;
            }
        } else {
            this.selectedFileName = null;
        }
    }


    /**
     * This function is responsible for uploading the selected file to the server using the SAS URL obtained from the backend. It encodes the filename to ensure that spaces and special characters are handled correctly in the URL. If a file is selected and a SAS URL is available, it calls the upload service to perform the upload and logs the response or any errors that occur during the process.
     */
    public updateUploadFiles() {
        // Encode the filename to handle spaces and special characters
        const encodedFileName = encodeURIComponent(this.selectedFileName ?? '');
        if (this.sasUrl) {
            const urlParts = this.sasUrl.split('?');
            const baseUrl = urlParts[0];
            const queryString = urlParts[1];
            const fullUrl = `${baseUrl}/${encodedFileName}?${queryString}`;
            if (this.SelectedFile) {
                this.uploadFile.updateTheFile(this.SelectedFile, fullUrl).subscribe({
                    next: (response) => {
                        this.fileUploadedFlag = true;
                    },
                    error: (error) => {
                        console.error('Error uploading file:', error);
                    }
                });
            } else {
                console.error('No file selected for upload.');
            }
        }
    }

    /**
     * This function is responsible for signaling the backend that the file upload process is complete. It constructs a payload containing the batch ID associated with the uploaded file and calls the upload service's `uploadComplete` method to notify the server. The function also includes error handling to log any issues that may arise during this process.
     */
    public submitUploadFile() {
        const payload = {
            batchId: this.batchId,
        }
        this.uploadFile.uploadComplete(payload).subscribe({
            next: () => {
                this._snackBar.open('File upload complete', 'Close', {
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                });
                this.isLoading = false;
                this.dialog.closeAll();
                this.fileUploadedFlag = false;
                this.sasUrl = '';
                this.batchId = '';
                this.fetchReviews();
            },
            error: (error) => {
                console.error('Error completing file upload:', error);
            }
        })
    }


}